import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getCableTypes, createCableType, updateCableType, deleteCableType, type CableType } from '../api/cableTypes'

const CABLE_TYPE_ICONS = [
  '🔌', '⚡', '🔗', '📡', '🌐', '📶', '🔋', '💡', '🔧', '📦', '🖥️', '📱', '🎛️', '🔊', '📺', '💻', '🖨️', '📷', '🎥', '⚙️'
]

const CABLE_TYPE_COLORS = [
  '#FFD700', '#32CD32', '#FF6347', '#4169E1', '#8B4513', '#FF4500', '#228B22', '#708090', '#808080', '#FF69B4', '#00CED1', '#FF8C00', '#9370DB', '#20B2AA', '#FF1493', '#00FF7F', '#FFDAB9', '#DDA0DD', '#F0E68C', '#98FB98'
]

export default function CableTypesPage() {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [selectedCableType, setSelectedCableType] = useState<CableType | null>(null)

  const queryClient = useQueryClient()

  const { data: cableTypes = [], isLoading, error } = useQuery({
    queryKey: ['cableTypes'],
    queryFn: () => getCableTypes()
  })

  // Create form state
  const [createForm, setCreateForm] = useState({
    name: '',
    code: '',
    description: '',
    icon: '🔌',
    color: '#FFD700',
    sort_order: 0,
    is_active: true
  })

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    code: '',
    description: '',
    icon: '🔌',
    color: '#FFD700',
    sort_order: 0,
    is_active: true
  })

  // Close all modals
  const closeAllModals = () => {
    setShowCreateForm(false)
    setShowEditForm(false)
    setSelectedCableType(null)
  }

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeAllModals()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // Mutations
  const createCableTypeMutation = useMutation({
    mutationFn: createCableType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cableTypes'] })
      setShowCreateForm(false)
      setCreateForm({
        name: '',
        code: '',
        description: '',
        icon: '🔌',
        color: '#FFD700',
        sort_order: 0,
        is_active: true
      })
    },
    onError: (error) => {
      console.error('Error creating cable type:', error)
      alert('Ошибка при создании типа кабеля')
    }
  })

  const updateCableTypeMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CableType> }) => 
      updateCableType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cableTypes'] })
      setShowEditForm(false)
      setSelectedCableType(null)
    },
    onError: (error) => {
      console.error('Error updating cable type:', error)
      alert('Ошибка при обновлении типа кабеля')
    }
  })

  const deleteCableTypeMutation = useMutation({
    mutationFn: deleteCableType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cableTypes'] })
    },
    onError: (error) => {
      console.error('Error deleting cable type:', error)
      alert('Ошибка при удалении типа кабеля')
    }
  })

  // Event handlers
  const handleCreateClick = () => {
    setShowCreateForm(true)
  }

  const handleEditClick = (cableType: CableType) => {
    setSelectedCableType(cableType)
    setEditForm({
      name: cableType.name,
      code: cableType.code,
      description: cableType.description || '',
      icon: cableType.icon || '🔌',
      color: cableType.color || '#FFD700',
      sort_order: cableType.sort_order,
      is_active: cableType.is_active
    })
    setShowEditForm(true)
  }

  const handleDeleteClick = (cableType: CableType) => {
    if (confirm(`Вы уверены, что хотите удалить тип кабеля "${cableType.name}"?`)) {
      deleteCableTypeMutation.mutate(cableType.id)
    }
  }

  const handleCreateSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    createCableTypeMutation.mutate(createForm)
  }

  const handleEditSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedCableType) return
    
    updateCableTypeMutation.mutate({
      id: selectedCableType.id,
      data: editForm
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">🔌 Типы кабелей</h1>
        </div>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Загрузка типов кабелей...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">🔌 Типы кабелей</h1>
        </div>
        <div className="text-center py-8 text-red-600">
          Ошибка загрузки типов кабелей: {error instanceof Error ? error.message : 'Неизвестная ошибка'}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">🔌 Типы кабелей</h1>
        <button
          onClick={handleCreateClick}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
        >
          ➕ Добавить тип
        </button>
      </div>

      {/* Cable Types Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Название
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Код
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Порядок
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {cableTypes.map((cableType) => (
              <tr key={cableType.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {cableType.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                    {cableType.code}
                  </code>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {cableType.sort_order}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-1">
                    <button
                      onClick={() => handleEditClick(cableType)}
                      className="action-icon-btn edit"
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteClick(cableType)}
                      className="action-icon-btn delete"
                      title="Удалить"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cableTypes.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Типы кабелей не найдены. Создайте первый тип кабеля.
        </div>
      )}

      {/* Create Form Modal */}
      {showCreateForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeAllModals()
            }
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Создать новый тип кабеля</h3>
            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Название *</label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    required
                    placeholder="Например: ВВГнг"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Код *</label>
                  <input
                    type="text"
                    value={createForm.code}
                    onChange={(e) => setCreateForm({ ...createForm, code: e.target.value })}
                    required
                    placeholder="Например: vvgng"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Описание</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  rows={3}
                  placeholder="Описание типа кабеля..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Иконка</label>
                  <div className="grid grid-cols-10 gap-1">
                    {CABLE_TYPE_ICONS.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setCreateForm({ ...createForm, icon })}
                        className={`w-8 h-8 rounded border text-lg ${
                          createForm.icon === icon 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Цвет</label>
                  <div className="grid grid-cols-5 gap-1">
                    {CABLE_TYPE_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setCreateForm({ ...createForm, color })}
                        className={`w-8 h-8 rounded border-2 ${
                          createForm.color === color 
                            ? 'border-blue-500' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={createForm.is_active}
                    onChange={(e) => setCreateForm({ ...createForm, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium">Активен</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeAllModals}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={createCableTypeMutation.isPending}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {createCableTypeMutation.isPending ? 'Создание...' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Form Modal */}
      {showEditForm && selectedCableType && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeAllModals()
            }
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Редактировать тип кабеля</h3>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Название *</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Код *</label>
                  <input
                    type="text"
                    value={editForm.code}
                    onChange={(e) => setEditForm({ ...editForm, code: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Описание</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Порядок сортировки</label>
                  <input
                    type="number"
                    value={editForm.sort_order}
                    onChange={(e) => setEditForm({ ...editForm, sort_order: parseInt(e.target.value) || 0 })}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Иконка</label>
                  <div className="grid grid-cols-10 gap-1">
                    {CABLE_TYPE_ICONS.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setEditForm({ ...editForm, icon })}
                        className={`w-8 h-8 rounded border text-lg ${
                          editForm.icon === icon 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Цвет</label>
                  <div className="grid grid-cols-5 gap-1">
                    {CABLE_TYPE_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setEditForm({ ...editForm, color })}
                        className={`w-8 h-8 rounded border-2 ${
                          editForm.color === color 
                            ? 'border-blue-500' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium">Активен</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeAllModals}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={updateCableTypeMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {updateCableTypeMutation.isPending ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

